import { ValidationErrors } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { connect } from "react-redux";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { thunkCreatePaymentInteraction, thunkGetCustomerBonusInfo } from "../../store/actions/TouchpointActions";
import { OperationalUnitType } from "../../store/models/OperationalUnitDto";
import { AdvantageType, CouponDto, EmbeddedCouponDto } from "../../store/models/TouchpointDto";
import { IssuedCouponDto } from "../../store/models/CouponTemplateDto";
import { IStore } from "../../store/rootReducer";
import { formatEuro } from "../app/Utils";
import { PaliFinalFormSelect } from "../pali/PaliFinalFormSelect";
import { BonusXpressPanel } from "./BonusXpressPanel";
import { CouponPanel } from "./CouponPanel";
import { receiptDetailExamplesTamaris } from "./ReceiptDetailExamplesTamaris";
import { receiptDetailExamples } from "./ReceiptDetailExamples";
import { receiptDetailExamplesMarcopolo } from "./ReceiptDetailExamplesMarcopolo";
import { receiptDetailExamplesNutripur } from "./ReceiptDetailExamplesNutripur";
import { receiptDetailExamplesToom } from "./ReceiptDetailExamplesToom";
import ReceiptSimulator from "./ReceiptSimulator";
import { ReceiptDetailPanel } from "./ReceiptDetailPanel";
import { ReceiptDetailPanelTamaris } from "./ReceiptDetailPanelTamaris";
import { ReceiptDetailPanelMarcopolo } from "./ReceiptDetailPanelMarcopolo";
import { ReceiptDetailPanelNutripur } from "./ReceiptDetailPanelNutripur";
import { ReceiptDetailPanelToom } from "./ReceiptDetailPanelToom";
import { pathToTheme } from "../../themes/SwitchTheme";

type ThunkProps = ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export type CashDeskSimulatorProps = {
  externalUnitNumber: string;
  selectedCoupons?: CouponDto[];
  selectedCoupon?: IssuedCouponDto;
  cashValue?: number;
  receiptDetailIds: string[];
};

// Calculate savings from new API-based coupon
export const calculateNewCouponSavings = (coupon: IssuedCouponDto, paymentAmount: number): number => {
  const template = coupon.couponTemplate;
  if (!template || !paymentAmount) return 0;
  
  switch (template.valueType) {
    case "PERCENTAGE":
      return (paymentAmount * (template.discountPercentage || 0)) / 100;
    case "FIXED_AMOUNT":
      return Math.min(template.discountAmount || 0, paymentAmount);
    case "FREE_SHIPPING":
    case "GIFT":
      return 0;
    default:
      return 0;
  }
};

export const calculatePaymentAmountWithCouponAndXpress = (
  paymentAmount: number,
  cashValue?: number,
  selectedCoupons?: CouponDto[],
  selectedCoupon?: IssuedCouponDto
) => {
  let amount = paymentAmount;
  
  // Apply old coupon system discounts
  if (selectedCoupons && selectedCoupons.length > 0) {
    amount = calculatePaymentAmountWithCoupons(paymentAmount, selectedCoupons);
  }
  
  // Apply new API coupon discount
  if (selectedCoupon) {
    const newCouponSavings = calculateNewCouponSavings(selectedCoupon, amount);
    amount = amount - newCouponSavings;
  }
  
  // Apply BonusXpress discount
  return amount - (cashValue || 0);
};

export const calculatePaymentAmountWithCoupons = (paymentAmount: number, selectedCoupons?: CouponDto[]) => {
  let cashValue =
    selectedCoupons?.reduce((cashValue, coupon) => cashValue + (calculateCoupon(paymentAmount, coupon) || 0), 0) || 0;
  return paymentAmount - cashValue;
};

export const calculateCoupon = (amount: number, coupon: CouponDto) => {
  if (coupon?.advantage.advantageValue) {
    if (coupon.advantage.advantageType === AdvantageType.PERCENT)
      return (amount * coupon.advantage.advantageValue) / 100;
    if (coupon.advantage.advantageType === AdvantageType.EURO)
      return amount >= 0 ? coupon.advantage.advantageValue : -coupon.advantage.advantageValue;
  }
};

const calculateCoupons = (paymentAmount: number, coupons?: CouponDto[]): EmbeddedCouponDto[] | undefined =>
  coupons?.map((coupon) => ({
    couponCode: coupon.couponCode,
    cashValue: calculateCoupon(paymentAmount, coupon),
  }));

const calculateAdvantages = (values: CashDeskSimulatorProps) => {
  const paymentAmount = calculatePaymentAmount(values.receiptDetailIds);
  const advantageTexts = values.selectedCoupons?.map((coupon) => calculateAdvantage(paymentAmount, coupon));
  return advantageTexts?.join(" | ");
};

const calculateAdvantage = (paymentAmount: number, coupon: CouponDto) => {
  if (coupon?.advantage.advantageValue) {
    if (coupon.advantage.advantageType === "PERCENT")
      return formatEuro((paymentAmount * coupon.advantage.advantageValue) / 100);
    if (coupon.advantage.advantageType === "EURO") return formatEuro(coupon.advantage.advantageValue);
    if (coupon.advantage.advantageType === "OFFER") return coupon.advantage.advantageText;
  }
};

const getReceiptDetailPanel = () => {
  const theme = pathToTheme();
  if (theme === "tamaris") return <ReceiptDetailPanelTamaris />;
  if (theme === "marcopolo") return <ReceiptDetailPanelMarcopolo />;
  if (theme === "nutripur") return <ReceiptDetailPanelNutripur />;
  if (theme === "toom") return <ReceiptDetailPanelToom />;
  return <ReceiptDetailPanel />;
};

export function calculatePaymentAmount(receiptDetailIds: string[]) {
  const currentTheme = pathToTheme();
  let productData = receiptDetailExamples;
  if (currentTheme === "tamaris") {
    productData = receiptDetailExamplesTamaris;
  } else if (currentTheme === "marcopolo") {
    productData = receiptDetailExamplesMarcopolo;
  } else if (currentTheme === "nutripur") {
    productData = receiptDetailExamplesNutripur;
  } else if (currentTheme === "toom") {
    productData = receiptDetailExamplesToom;
  }
  return productData
    .filter((detail) => receiptDetailIds.includes(detail.itemInfo.refNumber))
    .reduce((salesPrice, detail) => salesPrice + Number(detail.salesPrice), 0);
}

export class CashDeskSimulator extends React.Component<ThunkProps> {
  validate = (cashDeskSimulator: CashDeskSimulatorProps) => {
    const errors: ValidationErrors = {};
    if (!cashDeskSimulator.externalUnitNumber) {
      errors.externalUnitNumber = "Filiale erforderlich";
    }
    return errors;
  };

  initialValues: CashDeskSimulatorProps = {
    externalUnitNumber: "",
    selectedCoupons: undefined,
    cashValue: undefined,
    receiptDetailIds: [],
  };

  render() {
    const {
      thunkCreatePaymentInteraction,
      thunkGetCustomerBonusInfo,
      operationalUnits,
      customerBonusInfo,
      interactionTimestamp,
      customer,
    } = this.props;
    if (interactionTimestamp) return <ReceiptSimulator />;
    return (
      <Form
        validate={this.validate}
        onSubmit={(values) => {
          const paymentAmount = calculatePaymentAmount(values.receiptDetailIds);
          const coupons = calculateCoupons(paymentAmount, values.selectedCoupons);

          thunkCreatePaymentInteraction(
            customer.customerNumber,
            customer.externalCustomerId,
            values.externalUnitNumber,
            paymentAmount,
            values.receiptDetailIds,
            coupons,
            values.selectedCoupons,
            values.cashValue,
            values.selectedCoupon
          );
        }}
        mutators={{
          clearCashValue: (args, state, utils) => {
            utils.changeValue(state, "cashValue", () => undefined);
          },
        }}
        initialValues={this.initialValues}
        render={({ form, handleSubmit, submitting, hasValidationErrors, values }) => (
          <form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <PaliFinalFormSelect
                  name="externalUnitNumber"
                  label="Filiale auswählen *"
                  options={[{ label: "bitte wählen...", value: "" }].concat(
                    operationalUnits
                      .filter((operationalUnit) => operationalUnit.unitType === OperationalUnitType.BRANCH_STORE)
                      .map((operationalUnit) => ({
                        label: operationalUnit.unitName,
                        value: operationalUnit.externalUnitNumber,
                      }))
                  )}
                  onChange={(e) => {
                    thunkGetCustomerBonusInfo(customer.customerNumber, customer.externalCustomerId, e.target.value);
                  }}
                />
              </Col>
            </Row>

            {getReceiptDetailPanel()}

            <CouponPanel customerBonusInfo={customerBonusInfo} form={form} />

            {values.selectedCoupons && (
              <Row>
                <Col md={9}>
                  <FormGroup>
                    <Label>Ihr Vorteil</Label>
                    <Input type="text" readOnly={true} value={calculateAdvantages(values)} />
                  </FormGroup>
                </Col>
              </Row>
            )}

            <BonusXpressPanel customerBonusInfo={customerBonusInfo} values={values} />
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Rechnungssumme</Label>
                  <Input
                    type="text"
                    readOnly={true}
                    value={formatEuro(
                      calculatePaymentAmountWithCouponAndXpress(
                        calculatePaymentAmount(values.receiptDetailIds),
                        values.cashValue,
                        values.selectedCoupons,
                        values.selectedCoupon
                      )
                    )}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Button type="submit" className="mt-3" disabled={submitting || hasValidationErrors}>
                  Bezahlen
                </Button>
              </Col>
            </Row>
          </form>
        )}
      />
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ thunkGetCustomerBonusInfo, thunkCreatePaymentInteraction }, dispatch);

const mapStateToProps = (state: IStore) => ({
  customer: state.customer.customer,
  operationalUnits: state.operationalUnit.operationalUnits,
  customerBonusInfo: state.touchpoint.customerBonusInfo,
  interactionTimestamp: state.touchpoint.interactionTimestamp,
});

export default connect(mapStateToProps, mapDispatchToProps)(CashDeskSimulator);
